import React from 'react';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { ReactComponent as CompleteCircle } from '$/images/completeCircle.svg';
import { ME } from '$/GraphQL/queries/Users';
import {
  useResendConfirmationEmailMutation,
  useSkipVerifyEmailMutation,
} from '$/GraphQL/schema/schema';
import { GraphQLError } from 'graphql';
import config from '$/config';
import { StatusMessage } from '$/components/ui/MessageText';
import { useMe } from '$/hooks/useMe';
import { doesExist } from '$/utils/string';
import UserMenu from '$/sections/Home/UserMenu';
import { css } from '@emotion/react';
import { useLocation } from '@reach/router';
import { rolesPermissionsMatrix } from '$/utils/pathConfig';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

const ContentStyleComponent = styled.div`
  ${tw`
        flex
        flex-wrap
        flex-col
        justify-center
        items-center
        text-center
        tracking-wide
        leading-normal
        h-full
    `}
  .icon {
    ${tw`
            w-full
            flex
            justify-center
            py-3
        `}
  }
  .title {
    ${tw`
            w-full
            text-2xl
            leading-loose
            text-black
            py-3
        `}
  }
  .description {
    ${tw`
            w-full
            text-base
            text-gray-4
            font-light
            mb-8
        `}
  }
  .email {
    ${tw`
            text-lg
            font-semibold
            text-black
        `}
  }
  .btn-ok {
    ${tw`
            w-24
            appearance-none
            font-bold
            rounded-full
            text-sm
            text-white
            bg-blue-3
            px-5
            py-2
            mb-5
            focus:outline-none
            focus:shadow
        `}
  }
`;

const EmailConfirmationModal: React.FC = () => {
  const me = useMe();
  const location = useLocation();
  const [resendConfirmationEmailMutation, confirmationEmailResult] =
    useResendConfirmationEmailMutation();

  const [errors, setErrors] = React.useState<GraphQLError[]>([]);
  const [skipVerified] = useSkipVerifyEmailMutation();

  if (
    !doesExist(me) ||
    me.isVerified ||
    location.pathname === rolesPermissionsMatrix.profileEdit.path
  ) {
    return null;
  }

  let onlyMe = true;
  if (!me.team?.name) {
    // Haven't completed my profile yet
    onlyMe = false;
  }

  const resendConfirmationEmail = async (): Promise<void> => {
    try {
      await resendConfirmationEmailMutation();
      if (confirmationEmailResult && confirmationEmailResult.error) {
        setErrors(confirmationEmailResult.error.graphQLErrors.map((e) => e));
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal id='emailConfirmationModal' widthClass='min-w-32e' backdropClassName='h-100vh'>
      <React.Fragment>
        <Modal.Body>
          <ContentStyleComponent>
            <UserMenu
              onlyMe={onlyMe}
              onlyLogout={!onlyMe}
              noAvatar
              css={css`
                position: absolute;
                top: 1rem;
                right: 1rem;
              `}
            />
            <div className='icon'>
              <div className='w-auto'>
                <CompleteCircle />
              </div>
            </div>
            <div className='title'>Email confirmation</div>
            <div className='description'>
              We emailed <span className='email'>{me.email}</span> &mdash;
              <br />
              Please click the link inside to confirm
              <br />
              your new account.
            </div>

            <Button.submit
              className='text-lg mt-2'
              inverted
              onClick={(): void => void resendConfirmationEmail()}
              submitting={confirmationEmailResult.loading}
            >
              Send again
            </Button.submit>
            <StatusMessage
              errors={errors}
              message={confirmationEmailResult?.data?.resendConfirmationEmail}
            />
            {!config.isProduction && (
              <Button.rounded
                className='my-4'
                onClick={async (): Promise<void> => {
                  try {
                    await skipVerified({
                      refetchQueries: [{ query: ME }],
                    });
                  } catch (e) {
                    console.error(`Failed to skip verify`, e);
                  }
                }}
              >
                -- FORCE VERIFY NOW --
              </Button.rounded>
            )}
          </ContentStyleComponent>
        </Modal.Body>
      </React.Fragment>
    </Modal>
  );
};

export default EmailConfirmationModal;
